import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { changedRowClass, type RunPosition } from '@/features/ai-changeset/utils/changeset-view';
import { DraftTag } from '@/features/menu/components/DraftTag';
import { ChannelLines, flagText } from '@/features/menu/components/ProductDraftLines';
import type { ChoiceView, OptionView } from '@/features/menu/utils/menu-draft';
import { describeRules } from '@/features/menu/utils/menu-draft-text';
import { useT } from '@/i18n';
import { loc } from '@/utils/localized';
import { formatMoney } from '@/utils/money';
import { ListItem } from 'framework7-react';
import { memo } from 'react';

const optionChanged = (view: OptionView) =>
  !!view.status || !!view.name || !!view.rules || !!view.choiceOrder;

const OptionTitle = memo(function OptionTitle({ view }: { view: OptionView; slot: string }) {
  const id = view.option.id;
  return (
    <span>
      <span className={view.status === 'removed' ? 'quick-edit-old' : undefined}>
        {view.name ? (
          <ProposedValue start before={view.name.before} after={view.name.after} />
        ) : (
          loc(view.option.name)
        )}
      </span>
      <DraftTag status={view.status} testId={`product-option-tag-${id}`} />
    </span>
  );
});

function ChoiceLine({
  view,
  store,
  currency,
}: {
  view: ChoiceView;
  store: NavigationStore;
  currency: string;
}) {
  const q = useT().menuQuickEdit;
  const { choice, status, name, price } = view;
  const money = (value: number) => formatMoney(value, currency);
  return (
    <div
      data-quick-edit-target={view.changed ? choice.id : undefined}
      data-testid={`product-choice-${choice.id}`}
      style={{ position: 'relative' }}
    >
      {view.changed && (
        <ChangePulseMarker
          store={store}
          id={choice.id}
          testId={`product-choice-pulse-${choice.id}`}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span>
          <span className={status === 'removed' ? 'quick-edit-old' : undefined}>
            {name ? (
              <ProposedValue start before={name.before} after={name.after} />
            ) : (
              loc(choice.name)
            )}
          </span>
          <DraftTag status={status} testId={`product-choice-tag-${choice.id}`} />
          {status === 'new' && choice.isDefault && (
            <span className="ai-changed-text">{` · ${q.isDefault}`}</span>
          )}
          {view.flags.map(({ flag, after }) => (
            <span key={flag} className="ai-changed-text">{` · ${flagText(flag, after, q)}`}</span>
          ))}
          {choice.allergenCodes.length > 0 && (
            <span style={{ color: 'var(--f7-text-color-secondary)' }}>
              {` (${choice.allergenCodes.join(', ')})`}
            </span>
          )}
        </span>
        <span style={{ color: 'var(--f7-theme-color)', flexShrink: 0 }}>
          {price ? (
            <ProposedValue before={money(price.before)} after={money(price.after)} />
          ) : status === 'new' ? (
            <span className="ai-changed-text">{money(choice.price)}</span>
          ) : (
            money(choice.price)
          )}
        </span>
      </div>
      {view.channels.length > 0 && (
        <div className="quick-edit-detail-lines">
          <ChannelLines
            channels={view.channels}
            currency={currency}
            testId={`product-choice-channel-${choice.id}`}
          />
        </div>
      )}
    </div>
  );
}

const OptionChoices = memo(function OptionChoices({
  view,
  store,
  currency,
}: {
  view: OptionView;
  store: NavigationStore;
  currency: string;
  slot: string;
}) {
  const t = useT();
  return (
    <div style={{ whiteSpace: 'normal', color: 'var(--f7-list-item-text-text-color)' }}>
      {view.rules && (
        <div className="quick-edit-line" data-testid={`product-option-rules-${view.option.id}`}>
          <ProposedValue
            start
            before={describeRules(view.rules.before, t.menuQuickEdit)}
            after={describeRules(view.rules.after, t.menuQuickEdit)}
          />
        </div>
      )}
      {view.status === 'new' && (
        <div className="quick-edit-line" data-testid={`product-option-rules-${view.option.id}`}>
          <span className="ai-changed-text">{describeRules(view.rulesAfter, t.menuQuickEdit)}</span>
        </div>
      )}
      {view.choices.length === 0 ? (
        t.menu.noChoicesYet
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
          {view.choices.map((choice) => (
            <ChoiceLine key={choice.choice.id} view={choice} store={store} currency={currency} />
          ))}
        </div>
      )}
    </div>
  );
});

export function ProductOptionRow({
  view,
  run,
  store,
  currency,
  pickLabel,
  onOpen,
}: {
  view: OptionView;
  run: RunPosition | null;
  store: NavigationStore;
  currency: string;
  pickLabel: string;
  onOpen: (() => void) | null;
}) {
  const id = view.option.id;
  const classes = [
    'menu-item-row',
    view.status === 'removed' && 'menu-item-row--muted',
    changedRowClass(run),
  ];
  return (
    <ListItem
      key={id}
      // mediaItem so the `text` slot (the choices list below) actually
      // renders — a plain (non-media) list item hides text/subtitle.
      mediaItem
      after={pickLabel}
      link={onOpen ? '#' : undefined}
      onClick={onOpen ?? undefined}
      className={classes.filter(Boolean).join(' ')}
      data-quick-edit-target={optionChanged(view) ? id : undefined}
      data-view-row={id}
      data-testid={`product-option-${id}`}
    >
      {optionChanged(view) && (
        <ChangePulseMarker
          slot="root-start"
          store={store}
          id={id}
          testId={`product-option-pulse-${id}`}
        />
      )}
      <OptionTitle slot="title" view={view} />
      {/* The choices inside this option, one per line with their extra
          charge on the right, so each choice's price is visible at a
          glance under the option. */}
      <OptionChoices slot="text" view={view} store={store} currency={currency} />
    </ListItem>
  );
}
